import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import moment from 'moment';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext.js';
import amountFormat from '../../utils/amountFormat.js';
import { getPartnerOverview } from './overviewApi.js';
import {
    OverviewCardStyled,
    OverviewCardsStyled,
    OverviewChartTooltipStyled,
    OverviewChartWrapStyled,
    OverviewPageStyled,
    OverviewPeriodToggleStyled,
    OverviewSectionStyled,
} from './styled/OverviewStyled';

const PERIODS = ['day', 'week', 'month', 'all'] as const;
const PERIOD_STORAGE_KEY = 'affilers.overview.period';

type OverviewPeriod = (typeof PERIODS)[number];

function isOverviewPeriod(value: string | null): value is OverviewPeriod {
    return value !== null && (PERIODS as readonly string[]).includes(value);
}

function readStoredPeriod(): OverviewPeriod {
    try {
        const stored = localStorage.getItem(PERIOD_STORAGE_KEY);
        if (isOverviewPeriod(stored)) {
            return stored;
        }
    } catch {
        // ignore unavailable storage
    }
    return 'day';
}

function writeStoredPeriod(period: OverviewPeriod): void {
    try {
        localStorage.setItem(PERIOD_STORAGE_KEY, period);
    } catch {
        // ignore unavailable storage
    }
}

type OverviewCards = {
    clients_profit: string;
    partners_profit: string;
    total: string;
};

type OverviewSeriesPoint = {
    date: string;
    clients_profit: string;
    partners_profit: string;
};

type OverviewResponse = {
    period: OverviewPeriod;
    cards: OverviewCards;
    series: OverviewSeriesPoint[];
};

const EMPTY_CARDS: OverviewCards = {
    clients_profit: '0',
    partners_profit: '0',
    total: '0',
};

type ChartTooltipProps = {
    active?: boolean;
    label?: string;
    payload?: Array<{ dataKey?: string | number; value?: number; color?: string }>;
    getLangPath: (path: string) => string;
};

function ChartTooltip({ active, label, payload, getLangPath }: ChartTooltipProps) {
    if (!active || !payload?.length) {
        return null;
    }

    return (
        <OverviewChartTooltipStyled>
            <p className="tooltip-label">{label}</p>
            <ul className="tooltip-rows">
                {payload.map((entry) => {
                    const key = String(entry.dataKey);
                    const name =
                        key === 'clients'
                            ? getLangPath('overview.chart.clients')
                            : getLangPath('overview.chart.partners');
                    return (
                        <li key={key} className="tooltip-row">
                            <span className="tooltip-swatch" style={{ background: entry.color }} />
                            <span className="tooltip-name">{name}</span>
                            <span className="tooltip-value">${amountFormat(Number(entry.value) || 0)}</span>
                        </li>
                    );
                })}
            </ul>
        </OverviewChartTooltipStyled>
    );
}

function Overview() {
    const { getLangPath } = useLangContext();
    const [period, setPeriod] = useState<OverviewPeriod>(readStoredPeriod);
    const [loading, setLoading] = useState(false);
    const [cards, setCards] = useState<OverviewCards>(EMPTY_CARDS);
    const [series, setSeries] = useState<OverviewSeriesPoint[]>([]);

    const selectPeriod = useCallback((nextPeriod: OverviewPeriod) => {
        writeStoredPeriod(nextPeriod);
        setPeriod(nextPeriod);
    }, []);

    const load = useCallback(async (nextPeriod: OverviewPeriod) => {
        setLoading(true);
        try {
            const data: OverviewResponse = await getPartnerOverview({ period: nextPeriod });
            setCards(data.cards || EMPTY_CARDS);
            setSeries(Array.isArray(data.series) ? data.series : []);
        } catch {
            setCards(EMPTY_CARDS);
            setSeries([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(period);
    }, [load, period]);

    const chartData = useMemo(
        () =>
            series.map((point) => ({
                date: point.date,
                label:
                    period === 'day'
                        ? moment(point.date).utc().format('DD MMM')
                        : period === 'week'
                          ? moment(point.date).utc().format('WW/YYYY')
                          : moment(point.date).utc().format('MMM YYYY'),
                clients: Number(point.clients_profit) || 0,
                partners: Number(point.partners_profit) || 0,
            })),
        [period, series],
    );

    const cardItems = [
        {
            key: 'clients',
            label: getLangPath('overview.cards.clients'),
            value: cards.clients_profit,
        },
        {
            key: 'partners',
            label: getLangPath('overview.cards.partners'),
            value: cards.partners_profit,
        },
        {
            key: 'total',
            label: getLangPath('overview.cards.total'),
            value: cards.total,
        },
    ];

    return (
        <OverviewPageStyled>
            <div className="page-header">
                <span className="section-eyebrow">{getLangPath('overview.eyebrow')}</span>
                <h1 className="page-title">{getLangPath('overview.title')}</h1>
                <p className="page-desc">{getLangPath('overview.desc')}</p>
            </div>

            <OverviewSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('overview.kpi_step')}</span>
                        <h2 className="section-title">{getLangPath('overview.kpi_title')}</h2>
                        <p className="section-desc">{getLangPath('overview.kpi_desc')}</p>
                    </div>
                    <OverviewPeriodToggleStyled>
                        {PERIODS.map((item) => (
                            <Button
                                key={item}
                                type="button"
                                className="period-btn"
                                variant={period === item ? 'primary' : 'outline-secondary'}
                                disabled={loading}
                                onClick={() => selectPeriod(item)}
                            >
                                {getLangPath(`overview.periods.${item}`)}
                            </Button>
                        ))}
                    </OverviewPeriodToggleStyled>
                </div>

                <OverviewCardsStyled>
                    {cardItems.map((item) => (
                        <OverviewCardStyled key={item.key}>
                            <p className="card-label">{item.label}</p>
                            <p className="card-value">${amountFormat(Number(item.value))}</p>
                        </OverviewCardStyled>
                    ))}
                </OverviewCardsStyled>
            </OverviewSectionStyled>

            <OverviewSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <span className="section-eyebrow">{getLangPath('overview.chart_step')}</span>
                        <h2 className="section-title">{getLangPath('overview.chart_title')}</h2>
                        <p className="section-desc">{getLangPath('overview.chart_desc')}</p>
                    </div>
                </div>

                <OverviewChartWrapStyled>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={24} />
                            <YAxis tickLine={false} axisLine={false} width={48} />
                            <Tooltip
                                cursor={{ stroke: 'var(--color-white-20, rgba(255, 255, 255, 0.2))' }}
                                content={<ChartTooltip getLangPath={getLangPath} />}
                            />
                            <Legend
                                formatter={(value) =>
                                    value === 'clients'
                                        ? getLangPath('overview.chart.clients')
                                        : getLangPath('overview.chart.partners')
                                }
                            />
                            <Line
                                type="monotone"
                                dataKey="clients"
                                stroke="var(--color-primary, #5b8def)"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="partners"
                                stroke="var(--color-accent, #c9a227)"
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </OverviewChartWrapStyled>
            </OverviewSectionStyled>
        </OverviewPageStyled>
    );
}

export default Overview;
